import { createRef, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import http from 'lc-http';
import cookies from 'cookies-js';
import CtaButton from '../../ctaButton/ctaButton';
import * as defaults from '../constants';
import Tooltip from '../../toolTip/toolTip';
import Modal from '../../modalPopup/modalPopup';
import styles from './prequalCiab.module.scss';

function PrequalCiab({
  url,
  title,
  tooltip,
  ctaLabel,
  ciabCodePlaceholder,
  queryParams = '',
  ciabErrors,
  popupHead,
  popupBody,
  popupFooter,
  validationPopUpBodyForExpired,
  validationPopUpBodyForNotFound,
  validationPopUpButton,
  validationPopUpFooter,
  ctaVariant,
  ctaColor,
}) {
  const ciabCode = createRef();
  const [hasCiabCodeError, setHasCiabCodeError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCIABOpen, setIsModalCIABOpen] = useState(false);
  const [popupCIABBodyForExpired, setPopupCIABBodyForExpired] = useState(
    validationPopUpBodyForExpired
  );
  const [popupCIABBodyForNotFound, setPopupCIABBodyForNotFound] = useState(
    validationPopUpBodyForNotFound
  );

  const [popupCIABBodyErrCode, setPopupCIABBodyErrCode] = useState('');
  const [ciabInputValue, setCiabInputValue] = useState('');

  const handleCookie = (errCode, errMessage, ciabCodeValue) => {
    const unlimitedExpiry = new Date(2147483647 * 1000).toUTCString();

    let expiredCount = '';
    if (errCode === 410) {
      expiredCount = parseInt(cookies.get('ciab-410-error'), 10) || 0;
      cookies.set('ciab-410-error', expiredCount + 1, {
        expires: unlimitedExpiry,
      });
    }

    let tokenNotFoundCount = '';
    if (errCode === 404) {
      tokenNotFoundCount = parseInt(cookies.get('ciab-404-error'), 10) || 0;
      cookies.set('ciab-404-error', tokenNotFoundCount + 1, {
        expires: unlimitedExpiry,
      });
    }
    if (expiredCount + 1 >= 3 && errCode === 410) {
      setPopupCIABBodyForExpired(
        validationPopUpBodyForExpired.replace(`short_token`, ciabCodeValue)
      );
      setPopupCIABBodyErrCode(410);
      setIsModalCIABOpen(true);
    } else if (tokenNotFoundCount + 1 >= 3 && errCode === 404) {
      setPopupCIABBodyForNotFound(
        validationPopUpBodyForNotFound.replace(`short_token`, ciabCodeValue)
      );
      setPopupCIABBodyErrCode(404);
      setIsModalCIABOpen(true);
    } else {
      setHasCiabCodeError(errMessage);
    }
  };

  const handleInputMask = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z0-9]/g, '');

    const capitalizedValue = value.toUpperCase();

    // (3 characters, then a hyphen, 4 characters, then a hyphen, 3 characters)
    const formattedValue = capitalizedValue
      .replace(/^(.{3})(.{0,4})?(.*)?$/, (match, p1, p2, p3) =>
        [p1, p2, p3].filter(Boolean).join('-')
      )
      .substring(0, 12);

    setCiabInputValue(formattedValue);
  };

  const handleSubmit = () => {
    const ciabCodeValue = ciabCode ? ciabCode.current.value : '';

    // sanitize code since API doesn't accept hyphens
    const sanitizedCode = ciabCodeValue.replace(/-/g, '').toUpperCase();

    if (sanitizedCode.length !== 10) {
      setHasCiabCodeError(ciabErrors.ERROR_INVALID_CODE);
      return null;
    }

    let queryParam = '';

    const browserQueryParams = new URLSearchParams(
      window?.location?.search
    ).toString();
    if (browserQueryParams) {
      queryParam = `${queryParams}&${browserQueryParams}`;
    } else {
      queryParam = queryParams;
    }

    if (queryParams.includes('fundingcode=')) {
      queryParam = `${queryParam}${sanitizedCode}`;
    }

    return http
      .get(`/api/prequal-ciab?shortToken=${sanitizedCode}`)
      .then((response) => {
        const { data } = response;
        setHasCiabCodeError('');

        if (!sanitizedCode.match(/^[a-zA-Z0-9\d]{10,}$/)) {
          setHasCiabCodeError(ciabErrors.ERROR_INVALID_CODE);
        } else if (data.locker_token) {
          cookies.set('ciab-410-error', 0);
          cookies.set('ciab-404-error', 0);
          window.location.assign(
            `${url}?token=${data.locker_token}&flow=lead&${queryParam}`
          );
        } else {
          switch (data) {
            case 400:
              setHasCiabCodeError(ciabErrors.ERROR_400);
              break;
            case 404:
              handleCookie(404, ciabErrors.ERROR_404, sanitizedCode);
              break;
            case 410:
              handleCookie(410, ciabErrors.ERROR_410, sanitizedCode);
              break;
            case 500:
              setHasCiabCodeError(ciabErrors.ERROR_500);
              break;
            case 503:
              setHasCiabCodeError(ciabErrors.ERROR_503);
              break;
            default:
              setHasCiabCodeError(ciabErrors.ERROR_EXCEPTION);
          }
        }
      })
      .catch(() => []);
  };

  return (
    <div className={styles.wrapper}>
      {title && <h2 className={styles.title}>{title}</h2>}
      <div className={styles.ciabcodeWrapper}>
        <input
          ref={ciabCode}
          value={ciabInputValue}
          className={
            hasCiabCodeError
              ? `${styles.ciabCodeInputError} ${styles.ciabCodeInput}`
              : styles.ciabCodeInput
          }
          data-testid="ciabCode-id"
          placeholder={ciabCodePlaceholder}
          maxLength={12}
          onChange={handleInputMask}
        />
      </div>
      {hasCiabCodeError && (
        <span data-testid="error-ciabCode" className={styles.errorCiabCode}>
          {hasCiabCodeError}
        </span>
      )}
      {popupHead && (
        <div className={styles.tooltipPopup}>
          <span
            onClick={() => setIsModalOpen(true)}
            onKeyDown={() => setIsModalOpen(true)}
            data-testid="tooltip-popup"
            aria-label="find my code"
            role="button"
            tabIndex="0"
            className={styles.popupLabel}
          >
            {popupHead}
          </span>
        </div>
      )}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className={styles.popupBody}>
          {parse(`${popupBody}`)}
          <CtaButton
            onClick={() => setIsModalOpen(false)}
            onKeyDown={() => setIsModalOpen(false)}
            label="Found It!"
            className={styles.popupFound}
            tabIndex="0"
            type="button"
            data-id="direct-mail-button"
            data-analytics="direct-mail-button"
            name="button"
          />
          <div className={styles.popupFooter}>{popupFooter}</div>
        </div>
      </Modal>
      {(validationPopUpBodyForExpired || validationPopUpBodyForNotFound) && (
        <Modal
          isOpen={isModalCIABOpen}
          onClose={() => setIsModalCIABOpen(false)}
        >
          <div className={styles.ciabBody}>
            {popupCIABBodyErrCode === 410 &&
              parse(`${popupCIABBodyForExpired}`)}
            {popupCIABBodyErrCode === 404 &&
              parse(`${popupCIABBodyForNotFound}`)}
            <div className={styles.ciabLinks}>
              {validationPopUpButton && (
                <CtaButton
                  href={validationPopUpButton?.link}
                  label={validationPopUpButton?.label}
                  color={validationPopUpButton?.color}
                  type="button"
                  variant={validationPopUpButton?.variant}
                />
              )}
              <div className={styles.tooltipPopupDivider}>-OR-</div>
              {validationPopUpFooter && (
                <div className={styles.tooltipPopup}>
                  <span
                    onClick={() => setIsModalCIABOpen(false)}
                    onKeyDown={() => setIsModalCIABOpen(false)}
                    data-testid="ciab-error-popup"
                    aria-label="try a different rsvp code"
                    role="button"
                    tabIndex="0"
                    className={styles.popupLabel}
                  >
                    {validationPopUpFooter}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
      <CtaButton
        color={ctaColor}
        variant={ctaVariant}
        label={ctaLabel}
        fullWidth
        type="submit"
        data-testid="check-rate"
        className={styles.submitBtn}
        aria-label="submit-rate"
        onClick={handleSubmit}
      />
      {tooltip && (
        <div className={styles.tooltip}>
          <span className={styles.tooltipNote}>{tooltip.note}</span>
          {tooltip.helpNote && <Tooltip message={tooltip.helpNote} />}
        </div>
      )}
    </div>
  );
}

PrequalCiab.defaultProps = {
  url: defaults.url,
  title: defaults.title,
  tooltip: defaults.tooltip,
  ctaLabel: defaults.ctaLabel,
  ciabCodePlaceholder: defaults.ciabCodePlaceholder,
  ciabErrors: defaults.ciabErrors,
  queryParams: defaults.queryParams,
  validationPopUpBodyForExpired: defaults.validationPopUpBodyForExpired,
  validationPopUpBodyForNotFound: defaults.validationPopUpBodyForNotFound,
  validationPopUpButton: defaults.validationPopUpButton,
  validationPopUpFooter: defaults.validationPopUpFooter,
};

PrequalCiab.propTypes = {
  url: PropTypes.string,
  title: PropTypes.string,
  tooltip: PropTypes.shape({
    note: PropTypes.string,
    helpNote: PropTypes.string,
  }),
  ctaLabel: PropTypes.string,
  ciabCodePlaceholder: PropTypes.string,
  ciabErrors: PropTypes.object,
  queryParams: PropTypes.string,
  popupHead: PropTypes.string,
  popupBody: PropTypes.string,
  validationPopUpBodyForExpired: PropTypes.string,
  validationPopUpBodyForNotFound: PropTypes.string,
  validationPopUpButton: PropTypes.object,
  validationPopUpFooter: PropTypes.string,
  popupFooter: PropTypes.string,
};

export default PrequalCiab;
